import { Radio, Typography } from "@hellocarbo/lasagne";
import Card from "@ui/Card";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DisplayTypes } from "@/config";
import type { Comparison as TComparison } from "@/types";
import { getScopesAvailability } from "@/utils/comparison";

import ComparisonChart, { ComparisonChartRef } from "./ComparisonChart";
import ComparisonTable, { ComparisonTableRef } from "./ComparisonTable";

function Comparison({ comparisonData }: { comparisonData: TComparison }) {
  const { t } = useTranslation(["dashboard", "common"]);

  const [displayType, setDisplayType] = useState(DisplayTypes.total);
  const scopesAreAvailable = comparisonData
    ? getScopesAvailability(comparisonData)
    : false;
  const chartRef = useRef<ComparisonChartRef>(null);
  const tableRef = useRef<ComparisonTableRef>(null);

  const handleChartHover = (name: string | undefined) => {
    chartRef.current?.highlight(name);
  };

  const handleTableHover = (name: string | undefined) => {
    tableRef.current?.highlight(name);
  };

  if (!comparisonData) return <div>{t("common:loading")}</div>;

  return (
    <div>
      <Typography step="2" as="h2" autoSpacing>
        {t("dashboard:header")}
      </Typography>
      <Card>
        <div className="flex flex-row">
          <div className="basis-1/12">
            <Radio
              value={displayType}
              onValueChange={(newValue) =>
                setDisplayType(newValue as DisplayTypes)
              }
              label={t("dashboard:sortBy")}
              items={[
                {
                  label: t("dashboard:comparison.view.total"),
                  value: DisplayTypes.total,
                },
                {
                  label: t("dashboard:comparison.view.categories"),
                  value: DisplayTypes.byCategory,
                },
                ...(scopesAreAvailable
                  ? [
                      {
                        label: t("dashboard:comparison.view.scopes"),
                        value: DisplayTypes.byScope,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
          <div className="basis-11/12">
            <ComparisonChart
              data={comparisonData}
              type={displayType}
              ref={chartRef}
              onHover={handleTableHover}
            />
          </div>
        </div>
        <ComparisonTable
          data={comparisonData}
          type={displayType}
          ref={tableRef}
          onHover={handleChartHover}
        />
      </Card>
    </div>
  );
}

export default Comparison;
